import {useSyncExternalStore} from 'react';

export type ShellMode = 'desktop' | 'mobile';

/**
 * Which shell the app renders.
 *
 * The desktop shell is built on three assumptions that a phone breaks:
 * a pointer that can HOVER (the magnification wave), a pointer that is
 * PRECISE (3px traffic lights, 6px resize handles) and a viewport wide enough
 * to hold a floating window plus its shadow. Either a coarse pointer or a
 * narrow viewport is enough to invalidate all three, so either one alone
 * switches shells — a phone in landscape is still a phone.
 */
const MOBILE_QUERY = '(max-width: 860px), (pointer: coarse) and (max-width: 1180px)';

let cached: MediaQueryList | null = null;

function media(): MediaQueryList {
  if (!cached) cached = window.matchMedia(MOBILE_QUERY);
  return cached;
}

function subscribe(onStoreChange: () => void): () => void {
  const mql = media();
  mql.addEventListener('change', onStoreChange);
  // Rotating a phone can change the viewport without firing `change` on some
  // older WebKit builds, so the resize event is kept as a cheap safety net.
  window.addEventListener('resize', onStoreChange);
  return () => {
    mql.removeEventListener('change', onStoreChange);
    window.removeEventListener('resize', onStoreChange);
  };
}

function getSnapshot(): ShellMode {
  return media().matches ? 'mobile' : 'desktop';
}

export function useShellMode(): ShellMode {
  return useSyncExternalStore(subscribe, getSnapshot, () => 'desktop');
}
